/**
 * Migration Orchestrator - Simplified for Existing StoreRecord Integration
 * 
 * Simple script to run the complete migration process with minimal report disruption
 * since reports already use StoreRecord.id and shortName
 */

import { runMigration } from './migrateHierarchyData.js';
import { runMigrationV2 } from './migrateHierarchyDataV2.js';
import { validateMigration } from './validateMigration.js';
import { runLocationMigration } from './migrateToLocation.js';

/**
 * Complete migration process with validation - Simplified approach
 */
export async function runCompleteMigration(clientId = 'grm', options = {}) {
  const {
    skipValidation = false,
    dryRun = false,
    verbose = true
  } = options;

  console.log('🚀 Starting Simplified Migration Process');
  console.log(`📋 Client: ${clientId}`);
  console.log(`🎯 Approach: Update existing StoreRecord entries + Create OrgUnits`);
  console.log(`🔧 Options:`, { skipValidation, dryRun, verbose });
  console.log('='.repeat(60));

  try {
    // Step 1: Pre-migration validation (check existing stores)
    if (!skipValidation) {
      console.log('\n🔍 Step 1: Pre-migration checks...');
      await checkPreMigrationState(clientId);
    }

    // Step 2: Run simplified migration
    if (!dryRun) {
      console.log('\n📦 Step 2: Running simplified migration...');
      console.log('   - Creating OrgUnit records for areas');
      console.log('   - Updating existing StoreRecord entries with hierarchy');
      console.log('   - Creating ManagerAssignment records');
      console.log('   - Preserving Toast POS IDs in store metadata');
      
      const migrationResults = await runMigrationV2(clientId);
      
      if (verbose) {
        console.log('\n📊 Migration Results Summary:');
        console.log(`   OrgUnits Created: ${migrationResults.areas.created}`);
        console.log(`   StoreRecords Updated: ${migrationResults.stores.created}`);
        console.log(`   Manager Assignments: ${migrationResults.managers.created}`);
        
        const totalErrors = migrationResults.areas.errors.length + 
                           migrationResults.stores.errors.length + 
                           migrationResults.managers.errors.length;
        
        if (totalErrors > 0) {
          console.log(`   ⚠️  Total Errors: ${totalErrors}`);
        } else {
          console.log(`   ✅ No Errors`);
        }
      }

      // Step 3: Post-migration validation
      if (!skipValidation) {
        console.log('\n🔍 Step 3: Post-migration validation...');
        const validationResults = await validateMigration(clientId);
        
        if (!validationResults.summary.isValid) {
          throw new Error(`Migration validation failed with ${validationResults.summary.failedChecks} errors`);
        }
      }

      console.log('\n🎉 SIMPLIFIED MIGRATION COMPLETED SUCCESSFULLY!');
      console.log('\n✅ Benefits Achieved:');
      console.log('   - Existing reports continue working unchanged');
      console.log('   - Hierarchy structure now available for admin tools');
      console.log('   - Operational data preserved in structured metadata');
      console.log('   - Manager assignments ready for territory management');
      console.log('   - Toast POS integration maintained via metadata');
      console.log('='.repeat(60));
      
      return {
        success: true,
        migrationResults,
        validationResults: skipValidation ? null : await validateMigration(clientId)
      };

    } else {
      console.log('\n🔍 DRY RUN: Migration would execute but no changes made');
      console.log('   Expected actions:');
      console.log('   - Create ~5 OrgUnit records');
      console.log('   - Update ~25 existing StoreRecord entries');
      console.log('   - Create ~8 ManagerAssignment records');
      console.log('   - Preserve Toast POS IDs in metadata');
      return { success: true, dryRun: true };
    }

  } catch (error) {
    console.error('\n❌ MIGRATION FAILED!');
    console.error('Error:', error.message);
    console.error('Stack:', error.stack);
    
    return {
      success: false,
      error: error.message,
      stack: error.stack
    };
  }
}

/**
 * Check existing StoreRecord state
 */
async function checkPreMigrationState(clientId) {
  try {
    // Check existing stores
    const { API, graphqlOperation } = await import('aws-amplify');
    const { storesByClientSorted, orgUnitsByClientAndLevel } = await import('../graphql/queries');
    
    const storesResult = await API.graphql(
      graphqlOperation(storesByClientSorted, { clientId })
    );
    const existingStores = storesResult.data.storesByClientSorted?.items || [];
    
    const orgUnitsResult = await API.graphql(
      graphqlOperation(orgUnitsByClientAndLevel, { 
        clientId, 
        orgLevel: "ORGLEVEL_1" 
      })
    );
    const existingOrgUnits = orgUnitsResult.data.orgUnitsByClientAndLevel?.items || [];
    
    console.log(`📊 Pre-migration state:`);
    console.log(`   Existing StoreRecord entries: ${existingStores.length}`);
    console.log(`   Existing OrgUnit entries: ${existingOrgUnits.length}`);
    
    if (existingOrgUnits.length > 0) {
      console.log(`⚠️  Found ${existingOrgUnits.length} existing org units for client ${clientId}`);
      console.log('   Migration will attempt to update existing records if needed...');
    } else {
      console.log('✅ No existing org units - will create fresh OrgUnit records');
    }
    
    if (existingStores.length === 0) {
      console.warn('⚠️  No existing StoreRecord entries found!');
      console.warn('   This migration expects existing StoreRecord entries to update.');
      console.warn('   Please verify StoreRecord data exists before proceeding.');
    } else {
      console.log('✅ Found existing StoreRecord entries to update');
    }
    
  } catch (error) {
    console.log('⚠️  Could not check pre-migration state:', error.message);
    console.log('   Proceeding with migration...');
  }
}

/**
 * NEW: Location-based migration process
 * Migrates to the new Location table structure
 */
export async function runLocationBasedMigration(clientId = 'grm', options = {}) {
  const { verbose = true } = options;

  console.log('🚀 Starting Location-Based Migration Process');
  console.log(`📋 Client: ${clientId}`);
  console.log(`🎯 Approach: Create new Location records + LocationLaborTargets`);
  console.log('='.repeat(60));

  try {
    // Run the Location migration
    console.log('\n📦 Running Location migration...');
    console.log('   - Creating Location records from listAreas2');
    console.log('   - Creating LocationLaborTargets for theoretical hours');
    console.log('   - Creating ManagerOrgAssignments');
    console.log('   - Creating HierarchyChangeLog for audit trail');
    
    const results = await runLocationMigration(clientId);
    
    if (verbose) {
      console.log('\n📊 Location Migration Results Summary:');
      console.log(`   Locations Created: ${results.locations.created}`);
      console.log(`   Labor Targets Created: ${results.laborTargets.created}`);
      console.log(`   Manager Assignments: ${results.managers.created}`);
      console.log(`   History Records: ${results.history.created}`);
      console.log(`   OrgUnits Created: ${results.orgUnits.created}`);
      
      const totalErrors = 
        results.locations.errors.length + 
        results.laborTargets.errors.length + 
        results.managers.errors.length +
        results.history.errors.length +
        results.orgUnits.errors.length;
      
      if (totalErrors > 0) {
        console.log(`   ⚠️  Total Errors: ${totalErrors}`);
        
        // Show error details
        if (results.locations.errors.length > 0) {
          console.log('\n   Location Errors:');
          results.locations.errors.forEach(err => {
            console.log(`     - ${err.store}: ${err.error}`);
          });
        }
        
        if (results.laborTargets.errors.length > 0) {
          console.log('\n   Labor Target Errors:');
          results.laborTargets.errors.forEach(err => {
            console.log(`     - ${err.store}: ${err.error}`);
          });
        }
      } else {
        console.log(`   ✅ No Errors`);
      }
    }

    console.log('\n🎉 LOCATION MIGRATION COMPLETED SUCCESSFULLY!');
    console.log('\n✅ Benefits Achieved:');
    console.log('   - Clean Location-based hierarchy established');
    console.log('   - Theoretical hours in dedicated LocationLaborTargets table');
    console.log('   - Manager assignments properly structured');
    console.log('   - Complete audit trail with HierarchyChangeLog');
    console.log('   - External systems (Toast/Aloha) properly mapped');
    console.log('='.repeat(60));
    
    return {
      success: true,
      migrationResults: results
    };

  } catch (error) {
    console.error('\n❌ LOCATION MIGRATION FAILED!');
    console.error('Error:', error.message);
    console.error('Stack:', error.stack);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Simplified presets for different use cases
 */
export const migrationPresets = {
  // Production migration - full validation, safe
  production: (clientId) => runCompleteMigration(clientId, {
    skipValidation: false,
    dryRun: false,
    verbose: true
  }),
  
  // Development migration - quick iteration  
  development: (clientId) => runCompleteMigration(clientId, {
    skipValidation: false,
    dryRun: false,
    verbose: true
  }),
  
  // Dry run - see what would happen
  dryRun: (clientId) => runCompleteMigration(clientId, {
    skipValidation: true,
    dryRun: true,
    verbose: true
  }),
  
  // Quick migration - minimal validation for testing
  quick: (clientId) => runCompleteMigration(clientId, {
    skipValidation: true,
    dryRun: false,
    verbose: false
  }),
  
  // NEW: Location-based migration
  location: (clientId = 'grm') => runLocationBasedMigration(clientId, {
    verbose: true
  })
};

/**
 * Usage Examples:
 * 
 * // Full production migration (recommended first run)
 * const result = await migrationPresets.production('grm');
 * 
 * // Quick development iteration
 * const result = await migrationPresets.development('grm');
 * 
 * // Preview what would happen
 * const result = await migrationPresets.dryRun('grm');
 * 
 * console.log('Migration completed:', result.success);
 * if (!result.success) {
 *   console.error('Migration error:', result.error);
 * }
 */